/**
 * HRBadgeSubscriber — pushes the HR sidebar counts into `useHrBadgesStore`.
 *
 * Two count groups:
 *   - Admin counts (HRAdmin only) — Phase-4 pattern below.
 *   - Personal "My Records" counts (every authenticated user) — a plain REST
 *     baseline (`refetchPersonalBaseline`); no role gate, no WS (no HR events
 *     exist for them), so they refresh on mount only.
 *
 * Admin pattern, same as TaskBadgeSubscriber:
 *   1. Role pre-check; skip the WS connection for non-HRAdmins.
 *   2. REST baseline once on mount + on each reconnect (no 30s polling).
 *   3. Subscribe to the `hr` dashboard hub. BadgeDelta events reduce
 *      into the zustand store via `applyDelta`; BadgeSnapshot (not yet
 *      populated server-side) would replace the baseline when it arrives.
 *
 * Counter semantics:
 *   - `pendingLeaveApprovals` → moved by LeaveApplied/Approved/Rejected/Cancelled.
 *   - `policiesAwaitingSignature` → moved by PolicySigned (publish call
 *     is deferred per the migration plan; counter currently maintained
 *     only by the REST baseline).
 *   - `employeesOnLeaveToday` / `contractsExpiring30d` → no event-driven
 *     consumer yet; baseline keeps them fresh on reconnect / role change.
 */

import { useCallback, useEffect, useRef } from 'react';
import dayjs from 'dayjs';
import {
  useEmployeeService,
  useLeaveService,
  usePolicyAssignmentsService,
  useStaffPortalLeaveService,
  useStaffPortalPolicyAssignmentService,
  useStaffPortalDutyService,
  useDomainDashboard,
  type EmployeeModelView,
  type BadgeDeltaMessage,
  type BadgeSnapshotMessage,
} from '@actschurch/ccms-sdk';
import { useEnrichedAuth } from '@/hooks/useEnrichedAuth';
import { LEAVE_STATUS, POLICY_ASSIGNMENT_STATUS } from '@/pages/app/HRManagement/utils/hrConstants';
import { useHrBadgesStore, type HrBadgeMetrics } from './hrBadgesStore';

const HR_ADMIN_ROLE = 'HRAdmin';
const DEBUG = import.meta.env.DEV;
const log = (...args: any[]) => {
  if (DEBUG) console.log('[HRBadgeSubscriber]', ...args); // eslint-disable-line no-console
};

export function HRBadgeSubscriber() {
  const { hasAnyRole } = useEnrichedAuth();
  const isHrAdmin = hasAnyRole([HR_ADMIN_ROLE]);

  const employeeService = useEmployeeService();
  const leaveService = useLeaveService();
  const assignmentsService = usePolicyAssignmentsService();

  // Staff self-service endpoints — power the personal "My Records" badges,
  // which every authenticated user sees (not just HRAdmins).
  const staffLeaveService = useStaffPortalLeaveService();
  const staffAssignmentService = useStaffPortalPolicyAssignmentService();
  const staffDutyService = useStaffPortalDutyService();

  const setFromMetrics = useHrBadgesStore((s) => s.setFromMetrics);
  const setPersonalMetrics = useHrBadgesStore((s) => s.setPersonalMetrics);
  const applyDelta = useHrBadgesStore((s) => s.applyDelta);
  const reset = useHrBadgesStore((s) => s.reset);

  const refetchBaseline = useCallback(async () => {
    try {
      const [employees, leaves, assignments] = await Promise.all([
        employeeService.getAllViews(1, 1000),
        leaveService.getAllViews(1, 1000),
        assignmentsService.list({ status: 'Pending' } as any),
      ]);

      const empItems = (employees as any)?.items ?? [];
      const leaveItems = (leaves as any)?.items ?? [];
      const today = dayjs().startOf('day');
      const window = today.add(30, 'day');

      const pendingLeaveApprovals = leaveItems.filter(
        (l: any) => l.statusID === LEAVE_STATUS.PENDING,
      ).length;

      const employeesOnLeaveToday = leaveItems.filter((l: any) => {
        if (l.statusID !== LEAVE_STATUS.APPROVED) return false;
        const start = l.startDate ? dayjs(l.startDate) : null;
        const end = l.endDate ? dayjs(l.endDate) : null;
        if (!start || !end) return false;
        return (start.isBefore(today) || start.isSame(today, 'day'))
          && (end.isAfter(today) || end.isSame(today, 'day'));
      }).length;

      const policiesAwaitingSignature = ((assignments as any[]) ?? []).length;

      const contractsExpiring30d = (empItems as EmployeeModelView[]).filter((e: any) => {
        if (!e.endDate) return false;
        const ed = dayjs(e.endDate);
        return (ed.isAfter(today) || ed.isSame(today, 'day')) && ed.isBefore(window);
      }).length;

      log('baseline', {
        pendingLeaveApprovals,
        employeesOnLeaveToday,
        policiesAwaitingSignature,
        contractsExpiring30d,
      });
      setFromMetrics({
        pendingLeaveApprovals,
        employeesOnLeaveToday,
        policiesAwaitingSignature,
        contractsExpiring30d,
      });
    } catch (err) {
      log('baseline fetch failed', err);
    }
  }, [employeeService, leaveService, assignmentsService, setFromMetrics]);

  useEffect(() => {
    if (!isHrAdmin) return;
    refetchBaseline();
  }, [isHrAdmin, refetchBaseline]);

  // Personal "My Records" baseline. Unlike the admin counts there are no HR
  // events for these, so this REST fetch is their only source — and it runs
  // for every authenticated user, not just HRAdmins.
  const refetchPersonalBaseline = useCallback(async () => {
    try {
      const [leaves, assignments, duties] = await Promise.all([
        staffLeaveService.getMyLeaves(),
        staffAssignmentService.getMyAssignments(),
        staffDutyService.getMyDuties(),
      ]);

      const myPendingLeaves = ((leaves as any[]) ?? []).filter(
        (l: any) => l.statusID === LEAVE_STATUS.PENDING,
      ).length;

      const myUnsignedPolicies = ((assignments as any[]) ?? []).filter(
        (a: any) =>
          a.status === POLICY_ASSIGNMENT_STATUS.PENDING ||
          a.status === POLICY_ASSIGNMENT_STATUS.OVERDUE,
      ).length;

      const myActiveDuties = ((duties as any[]) ?? []).length;

      // Leaves the approver sent back (duty cover or a question): the "My Leaves" badge.
      const myLeavesActionNeeded = (leaves ?? []).filter(
        (l) => l.statusID === LEAVE_STATUS.PENDING && !!l.openAsk && l.openAsk.isOpen !== false,
      ).length;

      log('personal baseline', { myPendingLeaves, myUnsignedPolicies, myActiveDuties, myLeavesActionNeeded });
      setPersonalMetrics({ myPendingLeaves, myUnsignedPolicies, myActiveDuties, myLeavesActionNeeded });
    } catch (err) {
      log('personal baseline fetch failed', err);
    }
  }, [staffLeaveService, staffAssignmentService, staffDutyService, setPersonalMetrics]);

  useEffect(() => {
    refetchPersonalBaseline();
  }, [refetchPersonalBaseline]);

  const { isConnected, requestBadgeSnapshot } = useDomainDashboard('hr', {
    autoConnect: isHrAdmin,
    autoSubscribe: false,
    onBadgeSnapshot: (msg: BadgeSnapshotMessage<HrBadgeMetrics>) => {
      if (msg.domain !== 'HR') return;
      log('snapshot', msg.snapshot);
      setFromMetrics(msg.snapshot ?? {});
    },
    onBadgeDelta: (msg: BadgeDeltaMessage<HrBadgeMetrics>) => {
      if (msg.domain !== 'hr') return;
      log('delta', msg.reason, msg.deltas);
      applyDelta(msg.deltas ?? {});
    },
  });

  const wasConnectedRef = useRef(false);
  useEffect(() => {
    if (!isHrAdmin) return;
    if (isConnected && !wasConnectedRef.current) {
      wasConnectedRef.current = true;
      return;
    }
    if (isConnected && wasConnectedRef.current) {
      log('reconnected → refetch baseline');
      refetchBaseline();
      void requestBadgeSnapshot();
    }
  }, [isHrAdmin, isConnected, refetchBaseline, requestBadgeSnapshot]);

  useEffect(() => () => reset(), [reset]);

  useEffect(() => {
    log('mounted', { isHrAdmin });
  }, [isHrAdmin]);

  return null;
}
